import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {getInitials, getColorFromName} from '../../utils/helpers'
import {Typography, Avatar, Chip, Select, MenuItem} from '@mui/material'
import {Link, useLocation, useParams} from 'react-router-dom'
import {CalendarToday, Person, Assignment, MedicalServices, People} from '@mui/icons-material'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {caregiverApi} from '../../services'
import {parseError} from '../../utils/helpers'
import {useMobile} from '../../hooks'
import {useNavigate} from 'react-router-dom'
import {SkeletonContainer, TextSkeleton, AvatarSkeleton, ChipSkeleton} from '../Skeletons'

export default () => {
    const [caregiver, setCaregiver] = useState(null)
    const [initials, setInitials] = useState(null)
    const [avatarColor, setAvatarColor] = useState(null)
    const {setMessageBox, branchId} = useGlobalStates()
    const [skeleton, setSkeleton] = useState(true)
    const [menuValue, setMenuValue] = useState('')
    const location = useLocation()
    const {caregiverId} = useParams()
    const {mobile} = useMobile()
    const navigate = useNavigate()

    useEffect(() => {
        caregiverApi
            .getBranchCaregiver(branchId, caregiverId)
            .then(({data}) => {
                setCaregiver(data)
                setSkeleton(false)
            })
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [])

    useEffect(() => {
        if (!caregiver?.avatar_url) {
            setInitials(getInitials(caregiver?.first_name + ' ' + caregiver?.last_name))
            setAvatarColor(getColorFromName(caregiver?.first_name + ' ' + caregiver?.last_name))
        }
    }, [caregiver])

    useEffect(() => {
        if (location.pathname) {
            setMenuValue(location.pathname)
        }
    }, [location.pathname])

    const menuItems = [
        {label: 'Schedule', icon: <CalendarToday />, path: `/caregivers-management/${caregiverId}/schedule`},
        {label: 'Service', icon: <MedicalServices />, path: `/caregivers-management/${caregiverId}/service`},
        {label: 'Personal Information', icon: <Person />, path: `/caregivers-management/${caregiverId}/personal-information`},
        {label: 'Assigned Client', icon: <People />, path: `/caregivers-management/${caregiverId}/assigned-client`},
        {label: 'Documents', icon: <Assignment />, path: `/caregivers-management/${caregiverId}/documents`},
    ]

    const isActive = path => {
        if (path === '/') {
            return location.pathname === '/'
        }
        return location.pathname.startsWith(path)
    }

    return (
        <>
            {mobile ? (
                <div className="mobile-container">
                    {skeleton ? (
                        <SkeletonContainer>
                            <div className="profile-container">
                                <div className="profile">
                                    <AvatarSkeleton size={56} />
                                    <div className="name-container">
                                        <TextSkeleton width={130} fontSize={18} />
                                        <TextSkeleton width={170} fontSize={14} />
                                    </div>
                                </div>
                                <ChipSkeleton width={64} height={32} />
                            </div>
                        </SkeletonContainer>
                    ) : (
                        caregiver && (
                            <div className="profile-container">
                                <div className="profile">
                                    {caregiver?.avatar_url ? (
                                        <Avatar src={caregiver?.avatar_url} style={{width: 56, height: 56}} />
                                    ) : (
                                        <Avatar style={{backgroundColor: avatarColor, width: 56, height: 56, fontSize: 28}}>{initials}</Avatar>
                                    )}
                                    <div className="name-container">
                                        <Typography variant="h7" color="text.primary">
                                            {caregiver?.first_name + ' ' + caregiver?.last_name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {caregiver?.email}
                                        </Typography>
                                    </div>
                                </div>
                                {caregiver?.status === 'active' ? (
                                    <Chip label="Active" color="success" variant="filled" />
                                ) : caregiver?.status === 'pending' ? (
                                    <Chip
                                        label="Pending Invitation"
                                        variant="filled"
                                        sx={theme => ({
                                            backgroundColor: theme.palette.warning.light,
                                            color: theme.palette.warning.contrastText,
                                        })}
                                    />
                                ) : (
                                    <Chip label="Deactivated" color="error" variant="filled" />
                                )}
                            </div>
                        )
                    )}
                    <br />
                    <Select value={menuValue} onChange={e => navigate(e.target.value)} fullWidth>
                        {menuItems.map(item => (
                            <MenuItem key={item.path} value={item.path}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            ) : (
                <div className="container">
                    {skeleton ? (
                        <SkeletonContainer>
                            <div className="profile">
                                <AvatarSkeleton size={64} />
                                <TextSkeleton width={150} fontSize={20} />
                                <TextSkeleton width={210} fontSize={16} />
                                <div className="chip">
                                    <Typography variant="caption" color="primary">
                                        CAREGIVER
                                    </Typography>
                                </div>
                                <ChipSkeleton width={64} height={32} />
                            </div>
                        </SkeletonContainer>
                    ) : (
                        caregiver && (
                            <div className="profile">
                                {caregiver?.avatar_url ? (
                                    <Avatar src={caregiver?.avatar_url} style={{width: 64, height: 64}} />
                                ) : (
                                    <Avatar style={{backgroundColor: avatarColor, width: 64, height: 64, fontSize: 30}}>{initials}</Avatar>
                                )}
                                <Typography variant="h6" color="text.primary" className="name">
                                    {caregiver?.first_name + ' ' + caregiver?.last_name}
                                </Typography>
                                <Typography variant="body1" color="text.primary" className="name">
                                    {caregiver?.email}
                                </Typography>
                                <div className="chip">
                                    <Typography variant="caption" color="primary">
                                        CAREGIVER
                                    </Typography>
                                </div>
                                {caregiver?.status === 'active' ? (
                                    <Chip label="Active" color="success" variant="filled" />
                                ) : caregiver?.status === 'pending' ? (
                                    <Chip
                                        label="Pending Invitation"
                                        variant="filled"
                                        sx={theme => ({
                                            backgroundColor: theme.palette.warning.light,
                                            color: theme.palette.warning.contrastText,
                                        })}
                                    />
                                ) : (
                                    <Chip label="Deactivated" color="error" variant="filled" />
                                )}
                            </div>
                        )
                    )}
                    <br />
                    {menuItems.map(({label, icon, path}) => (
                        <div key={label} className={`item-container ${isActive(path) ? 'active' : ''}`}>
                            <Link to={path} className={`item ${isActive(path) ? 'active' : ''}`}>
                                <div className="icon">{icon}</div>
                                <div className="item-label">{label}</div>
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </>
    )
}
